import React, { useContext, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { has, get, pick, isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import { decode } from 'he'

import { DateTimeDisplay, PageDescription } from '../../../../components'
import Table from '../../../../components/Table/next'
import ReportActions from '../../../../components/ReportActions'

import { ClicksTableContext } from './index'
import {
  exportDetailReport,
  getDetailReport,
} from '../../../../actions/detailReport'
import { State } from '../../../../reducers'
import { pruneObject } from '../../../../utils/objectManagement'

import { ClickDetail } from '../../../../typings/detailReport'
import { ReportStatus } from '../../../../typings/report'
import { DetailReportStore } from '../../../../typings/stores/DetailReportStore'

import styles from '../styles.module.css'

const ClicksTable = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const {
    filters,
    pagination: { size, page },
    sort,
    changePageSize,
    changePage,
    changeSort,
  } = useContext(ClicksTableContext)

  const report = useSelector<State, DetailReportStore>(
    state => state.detailReport
  )

  const exportStatus = useSelector<State, ReportStatus>(state => ({
    ...pick(state.detailReport.export, ['hasData', 'status', 'error']),
    loading: state.detailReport.export.status === 'fetching',
    hasError: !isEmpty(state.detailReport.export.error),
  }))

  useEffect(() => {
    dispatch(
      getDetailReport('clicks', {
        ...filters,
        ...pruneObject(sort),
        size,
        page,
      })
    )
  }, [sort, page, size])

  const backUrl = has(location, 'state.performanceReportUrl')
    ? location.state.performanceReportUrl
    : '/app/reports'

  return (
    <>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <Link to={backUrl} className={styles.backLink}>
            Back to Performance Report
          </Link>
          <h2>Clicks Details</h2>
        </div>
        <ReportActions
          className={styles.export}
          exportStatus={exportStatus}
          reportStatus={exportStatus}
          fetchExport={() =>
            dispatch(
              exportDetailReport('clicks', { ...filters, ...pruneObject(sort) })
            )
          }
        />
      </div>

      <PageDescription content="This report contains transactional click details for the data selected in the performance report." />

      <Table<ClickDetail[]>
        cols={[
          {
            text: 'Click ID',
            field: 'clickId',
          },
          {
            text: 'Click Date',
            sortable: true,
            field: 'clickDate',
            render: row =>
              row.clickDate && (
                <DateTimeDisplay datetime={DateTime.fromSQL(row.clickDate)} />
              ),
          },
          {
            text: 'Product',
            sortable: true,
            field: 'productName',
            render: row => row.productName && decode(row.productName),
          },
          {
            text: 'Advertiser',
            field: 'advertiserName',
          },
          {
            text: 'Subtracking',
            sortable: true,
            field: 'subtracking',
          },
          {
            text: 'IP',
            field: 'ip',
          },
        ]}
        data={has(report, 'data.clicks') && report.data.clicks}
        isLoading={report.status === 'fetching'}
        onSortChange={changeSort}
        sort={sort}
        pagination={{
          total: get(report, 'data.pagination.total'),
          pageSize: get(report, 'data.pagination.pageSize'),
          currentPage: get(report, 'data.pagination.currentPage'),
          onPageSizeUpdate: changePageSize,
          onPaginate: changePage,
        }}
      />
    </>
  )
}

export default ClicksTable
